<template>
  <v-layout class="restaurant-cajas-container">
    <v-container fluid>
      <v-card>
        <v-card-title>
          <span>{{ $t('cajas') }}</span>
          <v-spacer></v-spacer>
          <!--<v-btn color="green" class="white--text mr-5"
                           :disabled="effectiveTier < 2 && cajas.length >= 30"
                           v-if="hasEditPermissions"
                           @click="addCajas">Carga Múltiple</v-btn>-->
          <!-- {{ $t('caja.new') }} -->
          <v-btn
            color="green"
            class="white--text"
            :disabled="effectiveTier < 2 && cajas.length >= 30"
            v-if="settingUseCajas && hasEditPermissions"
            @click="createNewCaja"
            >{{ $t('caja.new') }}</v-btn
          >
        </v-card-title>
        <v-card-subtitle>
          {{ $t('cajas.subtitle') }}
        </v-card-subtitle>
        <v-card-text>
          <template v-if="!settingUseCajas">
            <p class="text-center my-4">
              <b>Modulo de Caja desactivado</b>
              <br />
              Para utilizar el modulo de Caja, vaya a la configuracion de su
              tienda, pestaña Pedidos, y active la opción Usar cajas.
            </p>
          </template>
          <template v-else-if="!cajas.length">
            {{ $t('cajas.empty') }}
          </template>
          <template v-else>
            <v-data-table
              sort-by="name"
              :sort-desc="false"
              :must-sort="true"
              :headers="headers"
              :items="cajas"
              :items-per-page="50"
              :calculate-widths="true"
              :dense="true"
            >
              <template v-slot:item.actions="{ item }">
                <div class="children-inline">
                  <v-btn icon v-if="hasEditPermissions" @click="edit(item)"
                    ><v-icon>edit</v-icon></v-btn
                  >
                  <!--<v-btn icon
                                           @click="remove" color="red"><v-icon>delete</v-icon></v-btn>-->
                  <v-switch
                    color="green"
                    class="ma-0"
                    v-model="item.enabled"
                    v-if="hasDisablePermissions"
                    :hide-details="true"
                    @change="changeVisible($event, item)"
                  ></v-switch>
                </div>
              </template>
            </v-data-table>
          </template>
        </v-card-text>
      </v-card>
    </v-container>
  </v-layout>
</template>

<script lang="ts">
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import draggable from 'vuedraggable';
  import { ICaja } from '../../interfaces/ICaja';
  import { IRestaurant } from '../../interfaces/IRestaurant';
  import NewCajaModal from '../modals/NewCajaModal.vue';
  import AddCajasModal from '../modals/AddCajasModal.vue';
  import CajasService from '../../api/services/cajas.service';
  import RestaurantsService from '../../api/services/restaurants.service';
  import ConfirmModal from '../modals/ConfirmModal.vue';
  import { vueData } from '../../VueData';
  import { AlertType } from '../../interfaces/IAlert';
  import { IBranch } from '../../interfaces/IBranch';
  import { PermissionType } from '../../enums/PermissionType';
  import { IUser } from '../../interfaces/IUser';
  import {
    b64toBlob,
    createQrBase64,
    createQrImage,
    importScript,
    loadImage,
    rotateCanvas,
  } from '../../utils';
  import ZebraUtils from '../../lib/print/ZebraUtils';
  import { Globals } from '../../modules/Globals';
  import { createBMP, drawArray } from '../../plugins/jsbmp';
  import { useRestaurant } from '@/composition/use-restaurant';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { getEffectiveTier } from '@/utils/billing-entitlements.utils';

  @Component({
    components: { draggable },
  })
  export default class RestaurantCajas extends Vue {
    @Prop() restaurant: IRestaurant;
    @Prop() user: IUser;
    @Prop() branch: IBranch;
    cajas: ICaja[] = [];
    restaurantsService = new RestaurantsService();
    restaurantHelper = useRestaurant();
    restaurantStore = useRestaurantStore();
    headers = [];

    async created() {
      this.headers = [
        { text: 'ID', value: 'name', width: '100%' },
        { text: '', value: 'actions', sorcaja: false, width: 250 },
      ];
    }

    mounted() {
      this.init();
    }

    get hasEditPermissions() {
      return vueData.hasPermission(PermissionType.BranchSettings);
    }

    get hasDisablePermissions() {
      return vueData.hasPermission(PermissionType.BranchSettings);
    }

    async init() {
      await this.loadCajas();
    }

    get effectiveTier(): number {
      return getEffectiveTier(this.restaurant);
    }

    get settingUseCajas() {
      return this.effectiveTier > 0 && (this.branch?.settingUseCajas ?? false);
    }

    @Watch('branch')
    onBranchChanged() {
      this.loadCajas();
    }

    async changeVisible(newValue: boolean, caja: ICaja) {
      try {
        if (newValue) await CajasService.activate(caja);
        else await CajasService.deactivate(caja);
      } catch (e) {
        vueData.addAlert(
          this.$tc('errors.statusChangeFailed'),
          AlertType.Error
        );
        caja.enabled = !newValue;
      }
    }

    createNewCaja() {
      this.$modal.show(
        NewCajaModal,
        {
          caja: {
            restaurantId: this.restaurant.id,
            branchId: this.branch.id,
            name: '',
            adminKey: '',
          },
        },
        {
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
        },
        {
          saved: () => {
            this.loadCajas();
          },
        }
      );
    }

    edit(caja: ICaja) {
      this.$modal.show(
        NewCajaModal,
        {
          caja: { ...caja, adminKey: '' },
        },
        {
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
        },
        {
          saved: () => {
            this.loadCajas();
          },
        }
      );
    }

    async loadCajas() {
      const cajas = await this.restaurantHelper.reloadCajas();
      this.cajas = cajas;
    }

    remove() {
      this.$modal.show(
        ConfirmModal,
        {
          title: this.$t('delete.title'),
          message: this.$t('delete.warning'),
        },
        {
          name: 'delete',
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 600,
          clickToClose: false,
        }
      );
    }
  }
</script>
